import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EMPTY_LIVE_QUESTION_NOTICE_STATE,
  FailedLiveQuestion,
  LiveQuestionNoticeState,
  readLiveQuestionNotices,
  writeLiveQuestionNotices,
} from './storage';

/** Why a question is being reported as one that will never be answered. */
export type LiveQuestionNoticeReason = 'FAILED' | 'STALLED';

export interface LiveQuestionNotice {
  questionId: string;
  question: string;
  reason: LiveQuestionNoticeReason;
}

interface LiveQuestionNotices {
  /** Failures outstanding for this case, oldest first. */
  failedNotices: LiveQuestionNotice[];
  /** True once the user has cleared this question's notice for good. */
  isDismissed: (questionId: string) => boolean;
  /** Clears a notice — for a failure or for a stalled question — permanently. */
  dismissNotice: (questionId: string) => void;
  /** Takes what the failure check found; already-known and cleared ids are ignored. */
  recordFailedQuestions: (questions: FailedLiveQuestion[]) => void;
}

/**
 * Owns the questions the panel reports as never going to be answered: the ones
 * the backend marked FAILED, and the dismissals that apply to those and to the
 * stalled questions the queue reports.
 *
 * Kept in localStorage per case, so a notice survives the panel unmounting on a
 * sidebar tab switch, and a dismissal survives a reload — the user clearing one
 * means they've read it, and bringing it back on the next visit would read as
 * the cross not having worked.
 */
export function useLiveQuestionNotices(caseId: string | undefined): LiveQuestionNotices {
  const [notices, setNotices] = useState(EMPTY_LIVE_QUESTION_NOTICE_STATE);

  // Updates come from a poll callback rather than an event handler, so they read
  // the current value through a ref instead of the render's closure, which by
  // then can be several polls out of date. The case that ref holds is tracked
  // alongside it, so a callback firing in the render between one case being
  // opened and its notices being read can't write the last case's notices under
  // the new one.
  const noticesRef = useRef(notices);
  const loadedCaseIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const stored = caseId ? readLiveQuestionNotices(caseId) : EMPTY_LIVE_QUESTION_NOTICE_STATE;
    loadedCaseIdRef.current = caseId;
    noticesRef.current = stored;
    setNotices(stored);
  }, [caseId]);

  const applyNotices = useCallback((caseIdToWrite: string, next: LiveQuestionNoticeState) => {
    const trimmed = writeLiveQuestionNotices(caseIdToWrite, next);
    noticesRef.current = trimmed;
    setNotices(trimmed);
  }, []);

  const recordFailedQuestions = useCallback(
    (questions: FailedLiveQuestion[]) => {
      if (!caseId || loadedCaseIdRef.current !== caseId) return;

      const { failedQuestions, dismissedQuestionIds } = noticesRef.current;
      const known = new Set([...failedQuestions.map((failed) => failed.questionId), ...dismissedQuestionIds]);
      const added = questions.filter((question) => !known.has(question.questionId));
      if (added.length === 0) return;

      applyNotices(caseId, {
        failedQuestions: [...failedQuestions, ...added.map(({ questionId, question }) => ({ questionId, question }))],
        dismissedQuestionIds,
      });
    },
    [applyNotices, caseId],
  );

  const dismissNotice = useCallback(
    (questionId: string) => {
      if (!caseId || loadedCaseIdRef.current !== caseId) return;

      const { failedQuestions, dismissedQuestionIds } = noticesRef.current;
      if (dismissedQuestionIds.includes(questionId)) return;

      applyNotices(caseId, {
        // Dropped from the failures as well as recorded, so the list the panel
        // renders doesn't depend on the dismissed ids surviving the cap.
        failedQuestions: failedQuestions.filter((failed) => failed.questionId !== questionId),
        dismissedQuestionIds: [...dismissedQuestionIds, questionId],
      });
    },
    [applyNotices, caseId],
  );

  const isDismissed = useCallback(
    (questionId: string) => notices.dismissedQuestionIds.includes(questionId),
    [notices.dismissedQuestionIds],
  );

  return {
    failedNotices: notices.failedQuestions.map(({ questionId, question }) => ({
      questionId,
      question,
      reason: 'FAILED' as const,
    })),
    isDismissed,
    dismissNotice,
    recordFailedQuestions,
  };
}
